import {onChangeCheckedHandler, handleAutocomplete, onChangeDate} from 'lib/dom'
import LocalizedDateTimePicker from 'lib/LocalizedDateTimePicker'
import {EnabledSwitch} from 'lib/ui/form/Switch'
import TextField from 'lib/ui/TextField'
import MuiTextField from '@material-ui/core/TextField'
import {Label} from 'lib/ui/typography'
import {Agenda} from 'Event/template/NiftyFifty/Dashboard/AgendaList'
import React from 'react'
import {Controller} from 'react-hook-form'
import {Autocomplete} from '@material-ui/lab'
import withStyles from '@material-ui/core/styles/withStyles'
import styled from 'styled-components'
import {spacing} from 'lib/ui/theme'
import {orderedIdsByPosition} from 'lib/list'
import {useConfig} from 'organization/Event/Configurable'
import {useNiftyFiftyTemplate} from 'Event/template/NiftyFifty'
import {useAttendeeVariables} from 'Event'
import {startOfMinute} from 'date-fns'

export default function Settings(props: {agenda: Agenda; id: string}) {
  const {agenda, id} = props
  const {
    form: {control, register, watch, errors},
  } = useConfig()
  const {
    speakers: {items: allSpeakers},
  } = useNiftyFiftyTemplate()
  const v = useAttendeeVariables()

  const availableSpeakerIds = orderedIdsByPosition(allSpeakers)
  const agendaSpeakerIds = agenda.speakers

  // In case a speaker has been removed but is still referenced here, we will
  // only include them in the list of available speakers.
  const speakerIds = agendaSpeakerIds.filter((value) =>
    availableSpeakerIds.includes(value),
  )

  const startDate = watch('startDate', agenda.startDate)

  return (
    <>
      <Controller
        name={`agendaList.items.${id}.isVisible`}
        control={control}
        defaultValue={agenda.isVisible}
        render={({value, onChange}) => (
          <EnabledSwitch
            checked={value}
            onChange={onChangeCheckedHandler(onChange)}
            arial-label="config visible switch"
          />
        )}
      />
      <Label>Event</Label>
      <TextField
        name={`agendaList.items.${id}.text`}
        defaultValue={v(agenda.text)}
        aria-label="agenda text"
        fullWidth
        inputProps={{
          ref: register,
        }}
      />
      <Label>Description</Label>
      <TextField
        name={`agendaList.items.${id}.description`}
        defaultValue={v(agenda.description || '')}
        aria-label="agenda description"
        fullWidth
        inputProps={{
          ref: register,
        }}
      />
      <Controller
        name={`agendaList.items.${id}.startDate`}
        control={control}
        defaultValue={agenda.startDate}
        render={({value, onChange}) => {
          return (
            <LocalizedDateTimePicker
              value={value}
              onClear={() => {
                onChange(null)
              }}
              onChange={onChangeDate(onChange)}
              fullWidth
              label="Start"
              minDate={new Date()}
              inputProps={{
                'aria-label': 'agenda start date',
              }}
            />
          )
        }}
      />
      <Controller
        name={`agendaList.items.${id}.endDate`}
        control={control}
        defaultValue={agenda.endDate}
        rules={{
          validate: {
            notBeforeStart: (value) =>
              !value ||
              !startDate ||
              startOfMinute(new Date(value)) >=
                startOfMinute(new Date(startDate)) ||
              'End date must be after start date',
          },
        }}
        render={({value, onChange}) => (
          <LocalizedDateTimePicker
            value={value}
            onClear={() => {
              onChange(null)
            }}
            onChange={onChangeDate(onChange)}
            fullWidth
            label="End"
            inputProps={{
              'aria-label': 'agenda end date',
            }}
            error={Boolean(errors.agendaList?.items?.[id]?.endDate?.message)}
            helperText={errors.agendaList?.items?.[id]?.endDate?.message}
          />
        )}
      />
      <Label>Link</Label>
      <TextField
        name={`agendaList.items.${id}.link`}
        defaultValue={v(agenda.link || '')}
        aria-label="agenda link"
        fullWidth
        inputProps={{
          ref: register,
        }}
      />
      <label>Speakers</label>
      <Controller
        control={control}
        name={`agendaList.items.${id}.speakers`}
        defaultValue={speakerIds}
        render={({value, onChange}) => (
          <StyledAutocomplete
            multiple
            filterSelectedOptions
            options={availableSpeakerIds}
            value={value}
            onChange={handleAutocomplete(onChange)}
            aria-label="speaker selector"
            noOptionsText="No Speakers"
            getOptionLabel={(option) => v(allSpeakers[option].name)}
            getOptionSelected={(option) => option === value}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'speaker input',
                }}
                InputProps={{
                  ...params.InputProps,
                }}
              />
            )}
            renderOption={(id) => <div>{v(allSpeakers[id].name)}</div>}
          />
        )}
      />
    </>
  )
}

const StyledAutocomplete = withStyles({
  root: {
    flex: 1,
    marginRight: spacing[3],
    marginBottom: 0,
  },
})(Autocomplete) as typeof Autocomplete

const StyledTextField = styled(MuiTextField)`
  margin-bottom: 0;
`
